import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Building2, Send, Clock, Zap, ArrowUpRight,
  CheckCircle, AlertTriangle, Timer, WifiOff, X, Cpu, Wifi
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatResponseTime(seconds) {
  if (!seconds) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

export default function DashboardPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushHealth, setPushHealth] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [scraperStatus, setScraperStatus] = useState(null);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/analytics/dashboard`, { headers, withCredentials: true });
        setAnalytics(res.data);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
      }
      setLoading(false);
    };
    const fetchPushHealth = async () => {
      try {
        const res = await axios.get(`${API}/gmail/watch/health`, { headers, withCredentials: true });
        setPushHealth(res.data);
      } catch {
        setPushHealth(null);
      }
    };
    fetchAnalytics();
    fetchPushHealth();
    const fetchScraperStatus = async () => {
      try {
        const res = await axios.get(`${API}/health`, { withCredentials: true });
        setScraperStatus(res.data);
      } catch { setScraperStatus(null); }
    };
    fetchScraperStatus();
    const iv = setInterval(fetchPushHealth, 60000);
    const iv2 = setInterval(fetchScraperStatus, 30000);
    return () => { clearInterval(iv); clearInterval(iv2); };
  }, [token]);

  // Unhealthy accounts = expired watch, needs reauth, or recent renewal error
  const unhealthyAccounts = (pushHealth?.accounts || []).filter(a => !a.healthy);
  const showBanner = !bannerDismissed && unhealthyAccounts.length > 0;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = [
    { label: 'LEADS PROCESSED', value: analytics?.total_processed || 0, icon: Activity, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { label: 'RESPONSES SENT', value: analytics?.total_sent || 0, icon: Send, color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: 'PENDING REVIEW', value: analytics?.total_pending || 0, icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10' },
    { label: 'AVG RESPONSE TIME', value: formatResponseTime(analytics?.avg_response_time), icon: Timer, color: 'text-red-400', bg: 'bg-red-500/10', isTime: true },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Gmail Push Health Alert Banner */}
      {showBanner && (
        <div
          className="mb-6 bg-red-500/10 border border-red-500/40 rounded-lg p-4 flex items-start gap-3 shadow-[0_0_20px_rgba(239,68,68,0.15)]"
          data-testid="push-health-alert-banner"
        >
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <WifiOff className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading text-sm font-bold text-red-300 uppercase tracking-wider" data-testid="push-health-alert-title">
                Gmail Push Notifications Issue
              </h3>
              <Badge className="text-[8px] font-bold tracking-wider uppercase border bg-red-500/20 text-red-300 border-red-500/30">
                {unhealthyAccounts.length} account{unhealthyAccounts.length > 1 ? 's' : ''} affected
              </Badge>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed mb-2">
              Lead delivery may be delayed or stopped for the following Gmail account{unhealthyAccounts.length > 1 ? 's' : ''}:
            </p>
            <ul className="space-y-1 mb-3" data-testid="push-health-alert-list">
              {unhealthyAccounts.map(a => (
                <li key={a.account_id} className="text-xs text-slate-200 flex items-center gap-2 font-mono" data-testid={`push-health-alert-${a.account_id}`}>
                  <span className="text-red-400">•</span>
                  <span className="truncate">{a.email}</span>
                  <span className="text-[10px] text-red-400 tracking-wider uppercase">
                    {a.needs_reauth ? 'Needs Reauth' : a.is_expired ? 'Watch Expired' : 'Issue'}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate('/settings')}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white font-bold text-[10px] tracking-widest uppercase h-8"
                data-testid="push-health-alert-fix-btn"
              >
                Fix in Settings <ArrowUpRight className="w-3 h-3 ml-1" />
              </Button>
              <Button
                onClick={() => setBannerDismissed(true)}
                size="sm"
                variant="ghost"
                className="h-8 text-slate-400 hover:text-white text-[10px] font-bold tracking-widest uppercase"
                data-testid="push-health-alert-dismiss-btn"
              >
                Dismiss
              </Button>
            </div>
          </div>
          <button
            onClick={() => setBannerDismissed(true)}
            className="text-slate-500 hover:text-white transition-colors shrink-0"
            aria-label="Close"
            data-testid="push-health-alert-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
          Response Monitor
        </h1>
        <p className="text-slate-400 text-sm mt-2">
          Auto-response engine overview. {analytics?.total_dealerships || 0} active dealerships.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.label}
              className="bg-[#0A0A0A] border-[#262626] p-6 hover:border-[#404040] transition-colors duration-300 animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">{stat.label}</p>
                  <p className={`font-heading ${stat.isTime ? 'text-3xl' : 'text-4xl'} font-bold text-white mt-2`}>{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.color}`} strokeWidth={1.5} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* VDP Scraper Status */}
      {scraperStatus?.vdp_scraper && (
        <Card
          className={`border p-4 mb-8 ${
            scraperStatus.vdp_scraper.mode === 'firecrawl_primary'
              ? 'bg-[#0A0A0A] border-[#262626]'
              : scraperStatus.vdp_scraper.mode === 'playwright_only'
              ? 'bg-red-500/5 border-red-500/30'
              : 'bg-orange-500/5 border-orange-500/30'
          }`}
          data-testid="vdp-scraper-status"
        >
          <div className="flex items-center gap-4">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              scraperStatus.vdp_scraper.mode === 'firecrawl_primary'
                ? 'bg-green-500/10'
                : scraperStatus.vdp_scraper.mode === 'playwright_only'
                ? 'bg-red-500/10'
                : 'bg-orange-500/10'
            }`}>
              {scraperStatus.vdp_scraper.mode === 'firecrawl_primary'
                ? <Wifi className="w-4 h-4 text-green-400" />
                : <Cpu className="w-4 h-4 text-orange-400" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold tracking-widest text-slate-500 uppercase">VDP Scraper</p>
                <Badge className={`text-[8px] font-bold tracking-wider uppercase border ${
                  scraperStatus.vdp_scraper.mode === 'firecrawl_primary'
                    ? 'bg-green-500/10 text-green-400 border-green-500/20'
                    : scraperStatus.vdp_scraper.mode === 'playwright_only'
                    ? 'bg-red-500/10 text-red-400 border-red-500/20'
                    : 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                }`}>
                  {scraperStatus.vdp_scraper.mode === 'firecrawl_primary' ? 'Firecrawl Active'
                   : scraperStatus.vdp_scraper.mode === 'playwright_only' ? 'Firecrawl Down'
                   : 'Degraded'}
                </Badge>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">{scraperStatus.vdp_scraper.reason}</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${scraperStatus.playwright === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
                Playwright
              </span>
              <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  !scraperStatus.vdp_scraper.firecrawl_exhausted && scraperStatus.vdp_scraper.firecrawl_failures < 6
                    ? 'bg-green-500' : 'bg-red-500'
                }`} />
                Firecrawl
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <Card className="bg-[#0A0A0A] border-[#262626] lg:col-span-7 p-6" data-testid="leads-by-type-chart">
          <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-6">
            Responses by Lead Type
          </h3>
          {analytics?.leads_by_type?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.leads_by_type}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="label" tick={{ fill: '#94A3B8', fontSize: 10 }} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#171717', border: '1px solid #262626', borderRadius: 8, color: '#F8FAFC' }} />
                <Bar dataKey="count" fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-500 text-sm">
              <div className="text-center">
                <Activity className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                <p>No response data yet. Simulate a lead to get started.</p>
              </div>
            </div>
          )}
        </Card>

        {/* Dealership breakdown */}
        <Card className="bg-[#0A0A0A] border-[#262626] lg:col-span-5 p-6" data-testid="dealership-breakdown">
          <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider mb-6">
            By Dealership
          </h3>
          {analytics?.leads_by_dealership?.length > 0 ? (
            <div className="space-y-3">
              {analytics.leads_by_dealership.map(dlr => (
                <div key={dlr.dealership_id} className="flex items-center justify-between p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-red-500" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-white">{dlr.name}</span>
                  </div>
                  <span className="font-heading text-lg font-bold text-white">{dlr.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-500 text-sm">
              <div className="text-center">
                <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                <p>Add dealerships to track responses</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Recent responses */}
      <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="recent-responses">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider">
            Recent Responses
          </h3>
          <button
            onClick={() => navigate('/responses')}
            className="text-xs font-bold tracking-wider text-red-500 hover:text-red-400 uppercase flex items-center gap-1 transition-colors"
            data-testid="view-all-responses-btn"
          >
            View All <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>

        {analytics?.recent_responses?.length > 0 ? (
          <div className="space-y-3">
            {analytics.recent_responses.slice(0, 5).map(lead => (
              <div
                key={lead.lead_id}
                onClick={() => navigate(`/responses/${lead.lead_id}`)}
                className="flex items-center gap-4 p-4 rounded-lg bg-[#0F0F0F] border border-[#1A1A1A] hover:border-[#333] cursor-pointer transition-all"
                data-testid={`recent-response-${lead.lead_id}`}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  lead.response_status === 'sent' ? 'bg-green-500' :
                  lead.response_status === 'pending_review' ? 'bg-orange-500' : 'bg-red-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{lead.customer_name}</span>
                    <Badge variant="outline" className="text-[9px] font-bold tracking-wider border-[#333] text-slate-400 uppercase">
                      {lead.lead_type_label || lead.lead_type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {lead.dealership_name} &middot; {lead.email_subject}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${
                    lead.response_status === 'sent' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                    lead.response_status === 'pending_review' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                    'bg-red-500/10 text-red-400 border-red-500/20'
                  }`}>
                    {lead.response_status === 'pending_review' ? 'Pending' : lead.response_status}
                  </Badge>
                  <span className="font-mono text-xs text-slate-400">
                    {formatResponseTime(lead.response_time_seconds)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-12 text-center">
            <Zap className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-500 text-sm">No responses yet. Add a dealership, seed templates, and simulate a lead.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
